import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { from } from "rxjs";
import { exhaustMap, map, catchError, mergeMap, tap } from "rxjs/operators";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";
import { TankGaugeService } from "src/app/shared/services/tank-gauges/tank-gauges.service";
import { AppState } from "../../reducers";

import * as TankGaugeActions from "../../actions/tank-gauges/tank-gauges.actions";
import * as MessagesActionTypes from "../../actions/messages/message.actions";
import { WarehouseService } from "src/app/shared/services/warehouses/warehouses.service";

@Injectable()
export class TankGaugeEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private tankgaugeService: TankGaugeService,
    private tableConfigService: TableConfigService,
    private warehouseService: WarehouseService,
    private translate: TranslateService
  ) {}
  
  getTankGauges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TankGaugeActions.getTankGauges),
      exhaustMap((action) =>
        this.tankgaugeService.getTankGauges(action.code, action.pageReq).pipe(
          map((result) =>
            TankGaugeActions.loadTankGauges({ response: result })
          ),
          catchError((error) => {
            const actions = [
              TankGaugeActions.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TankGauges.loadError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getWarehouses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TankGaugeActions.getWarehouses),
      exhaustMap((action) =>
        this.warehouseService
          .getWarehouses(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              TankGaugeActions.loadWarehouses({ response: result })
            ),
            catchError((error) => {
              const actions = [
                TankGaugeActions.setLoadingEndState(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "TankGauges.loadingWarehousesError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getTankGaugeType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TankGaugeActions.getTankGaugeTypes),
      exhaustMap((action) =>
        this.tankgaugeService.getTankGaugeTypes(action.pageReq).pipe(
          map((result) =>
            TankGaugeActions.loadTankGaugeTypes({ response: result })
          ),
          catchError((error) => {
            const actions = [
              TankGaugeActions.loadTankGaugeTypes({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TankGauges.loadTankGaugeTypesError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getTankGauge$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TankGaugeActions.getTankGauge),
      exhaustMap((action) =>
        this.tankgaugeService.getTankGauge(action.code).pipe(
          map((result) =>
            TankGaugeActions.loadTankGauge({ tankGauge: result })
          ),
          catchError((error) => {
            const actions = [
              TankGaugeActions.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TankGauge.loadTankGaugeError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  createTankGauge$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TankGaugeActions.createTankGauge),
      exhaustMap((action) =>
        this.tankgaugeService.createTankGauge(action.tankGauge).pipe(
          mergeMap((result) => [
            TankGaugeActions.resetCreation(),
            TankGaugeActions.getTankGauges({
              code: result?.result?.siteCode,
              pageReq: { realSize: true, offset: 0, limit: 999 },
            }),
          ]),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TankGauge.tankGaugeCreated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              TankGaugeActions.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TankGauge.tankgaugeCreateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  updateTankGauge$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TankGaugeActions.updateTankGauge),
      exhaustMap((action) =>
        this.tankgaugeService.updateTankGauge(action.tankGauge).pipe(
          map((result) => {
            return TankGaugeActions.getTankGauges({
              code: result?.result?.site?.code,
              pageReq: { realSize: true, offset: 0, limit: 999 },
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TankGauge.tankGaugeModeUpdated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              TankGaugeActions.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TankGauge.tankGaugeUpdatedFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Tank Gauge Mode Table Config
  getServiceModeTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TankGaugeActions.getTankGaugeTableConfig),
      exhaustMap((action) =>
        this.tableConfigService
          .getConfigSettings(action.options.code, action.options.settingType)
          .pipe(
            map((result) =>
              TankGaugeActions.loadTankGaugeTableConfig({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                TankGaugeActions.loadTankGaugeTableConfig({
                  response: [],
                }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "TableConfig.LoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Save Service Mode Table Config
  saveServiceModeTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TankGaugeActions.saveTankGaugeTableConfig),
      exhaustMap((action) =>
        this.tableConfigService.saveConfigSettings(action.config).pipe(
          mergeMap(() => [
            TankGaugeActions.setLoadingEndState(),
            MessagesActionTypes.setSnackbar(
              this.buildSnackbarObj(
                "TableConfig.SaveSuccess",
                SnackbarType.SUCCESS
              )
            ),
          ]),
          catchError((error: any) => {
            const actions = [
              TankGaugeActions.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TableConfig.SaveError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  buildSnackbarObj(message: string, snackbarType: SnackbarType) {
    return {
      snackbar: {
        message: this.translate.instant(message),
        type: snackbarType,
      },
    };
  }
}
